import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useLocaleNavigate } from '@/hooks/use-locale-navigate'
import { LocaleLink } from '@/components/LocaleLink'
import { User, Settings2, LogOut, Droplets, Tag, LifeBuoy } from 'lucide-react'
import { swimApi } from '@swim/api-client'
import { useSession, useInvalidateSession } from '@/hooks/use-session'
import { useNotify } from '@/hooks/use-notify'
import { useDropsBalance } from '@/hooks/use-drops'
import { useNotifications, NotificationBell, useMessages, MessagesIcon } from '@swim/ui'
import { Skeleton } from '@/components/ui/skeleton'

function DropIcon({ className = '' }: { className?: string }) {
  return (
    <Droplets
      className={`w-4 h-4 flex-shrink-0 ${className}`}
      aria-hidden
    />
  )
}

export function AvatarMenu() {
  const [open, setOpen] = useState(false)
  const [imageLoadFailed, setImageLoadFailed] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuItemsRef = useRef<HTMLElement[]>([])
  const { navigate } = useLocaleNavigate()
  const { pathname } = useLocation()
  const { user, isLoading } = useSession()
  const invalidateSession = useInvalidateSession()
  const notify = useNotify()
  const { t } = useTranslation('navigation')
  const { data: dropsData } = useDropsBalance()
  const dropsBalance = dropsData?.balance ?? 0

  const { notifications, unreadCount, loading, markRead, markAllRead } = useNotifications({
    apiBase: (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? '',
    getAuthHeader: () => null, // Storefront uses cookie auth
    enabled: !!user,
    sseEnabled: false, // SSE not built yet (Web-1kr2)
  })

  const { unreadThreadCount } = useMessages({
    apiBase: (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? '',
    getAuthHeader: () => null, // Storefront uses cookie auth
    enabled: false, // was: enabled: !!user — messages polling killed until the feature is built, see Web-z8o8
  })

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
      if (e.key === 'm' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        if (!user) {
          navigate('/sign-in')
        } else {
          setOpen((p) => !p)
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [user, navigate])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Roving focus: collect menu items and auto-focus the first one whenever
  // the panel opens. Re-runs on `open` only — the item list itself is
  // static per render (no conditional items), so no other deps needed.
  useEffect(() => {
    if (open && panelRef.current) {
      const items = Array.from(panelRef.current.querySelectorAll('[role="menuitem"]')) as HTMLElement[]
      menuItemsRef.current = items
      setFocusedIndex(0)
      items[0]?.focus()
    } else {
      menuItemsRef.current = []
      setFocusedIndex(-1)
    }
  }, [open])

  useEffect(() => {
    menuItemsRef.current.forEach((item, index) => {
      item.setAttribute('tabindex', index === focusedIndex ? '0' : '-1')
    })
  }, [focusedIndex])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleMenuKeydown = useCallback((e: KeyboardEvent) => {
    const itemCount = menuItemsRef.current.length
    if (itemCount === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex((prev) => {
          const next = prev < itemCount - 1 ? prev + 1 : 0
          menuItemsRef.current[next]?.focus()
          return next
        })
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : itemCount - 1
          menuItemsRef.current[next]?.focus()
          return next
        })
        break
      case 'Home':
        e.preventDefault()
        setFocusedIndex(0)
        menuItemsRef.current[0]?.focus()
        break
      case 'End':
        e.preventDefault()
        setFocusedIndex(itemCount - 1)
        menuItemsRef.current[itemCount - 1]?.focus()
        break
      default:
        break
    }
  }, [])

  useEffect(() => {
    const panel = panelRef.current
    if (open && panel) {
      panel.addEventListener('keydown', handleMenuKeydown)
      return () => panel.removeEventListener('keydown', handleMenuKeydown)
    }
  }, [open, handleMenuKeydown])

  async function handleSignOut() {
    setOpen(false)
    await swimApi.post('/auth/customer/sign-out', {})
    await invalidateSession()
    notify.success(t('chrome.avatar.toastSignedOut'))
    navigate('/')
  }

  // Session loading — show skeleton so no previous user's data flashes in chrome
  if (isLoading) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
      </div>
    )
  }

  // Unauthenticated state — single click goes straight to /sign-in, no dropdown
  if (!user) {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="group relative flex flex-col items-center">
          <button
            onClick={() => navigate('/sign-in')}
            aria-label={t('chrome.avatar.logIn')}
            className="
              w-10 h-10 rounded-full
              border-2 transition-all duration-200
              bg-sand-100 dark:bg-slate-700
              flex items-center justify-center
              border-transparent opacity-50
              group-hover:opacity-100 group-hover:border-ocean-500
            "
          >
            <User className="h-5 w-5 text-slate-500 dark:text-sand-400" />
          </button>

          {/* Hover tooltip */}
          <span
            aria-hidden
            className="
              absolute start-full ms-3 top-1/2 -translate-y-1/2
              flex items-center gap-3
              px-3 py-2 rounded-lg
              bg-slate-800 dark:bg-sand-100
              text-white dark:text-slate-800
              opacity-0 group-hover:opacity-100
              pointer-events-none
              transition-opacity duration-150
              shadow-sm
            "
          >
            <span className="text-sm font-medium font-sans whitespace-nowrap">
              {t('chrome.avatar.logIn')}
            </span>
            <kbd className="flex-shrink-0 px-1.5 py-0.5 rounded border border-white/30 dark:border-slate-800/30 text-[9px] font-mono leading-none opacity-80">
              M
            </kbd>
          </span>
        </div>
      </div>
    )
  }

  // Derive display initial from user name or email
  const initial = user?.name?.charAt(0).toUpperCase()
    ?? user?.email?.charAt(0).toUpperCase()
    ?? 'U'

  return (
    <div className="flex flex-col items-start gap-3">
      {/* MessagesIcon */}
      {user && (
        <MessagesIcon
          unreadCount={unreadThreadCount}
          onClick={() => navigate('/messages')}
          tooltipLabel="Messages"
          isActive={/\/messages/.test(pathname)}
        />
      )}

      {/* NotificationBell */}
      <NotificationBell
        notifications={notifications}
        unreadCount={unreadCount}
        loading={loading}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onViewAll={() => navigate('/notifications')}
        placement="right"
        tooltipLabel="Notifications"
        isActive={/\/notifications/.test(pathname)}
        activeClass="bg-slate-200/80 dark:bg-slate-700 text-slate-800 dark:text-sand-100"
      />

      {/* Avatar Menu */}
      <div ref={ref} className="relative flex flex-col items-center">

        {/* Menu panel */}
      {open && (
        <div
          ref={panelRef}
          role="menu"
          className="
            absolute bottom-0 start-full ms-2
            flex flex-col overflow-hidden rounded-2xl
            bg-white dark:bg-slate-900
            border border-slate-200 dark:border-slate-700
            shadow-lg z-30
            min-w-[288px]
          "
        >
          {/* Name + Drops balance */}
          <div className="px-4 pt-3 pb-2">
            {/* User name + email */}
            <div className="mb-3">
              {user?.name && (
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 font-sans truncate">
                  {user.name}
                </p>
              )}
              {user?.email && (
                <p className="text-[10px] font-mono tracking-wide uppercase text-slate-400 dark:text-slate-500 truncate mt-0.5">
                  {user.email}
                </p>
              )}
            </div>

            {/* Drops label — outside the chip */}
            <p className="text-[9px] font-mono tracking-widest uppercase text-slate-400 dark:text-slate-500 mb-1.5 px-1">
              {t('chrome.avatar.dropsLabel')}
            </p>

            {/* Drops chip — icon + count only, lean */}
            <div className="
              flex items-center gap-2
              px-3 py-2.5 rounded-xl
              bg-sand-100 dark:bg-slate-800
              border border-sand-300 dark:border-slate-700
            ">
              <DropIcon className="text-ocean-400 dark:text-ocean-300" />
              <span className="text-lg font-bold text-ocean-400 dark:text-ocean-300 font-display tabular-nums leading-none">
                {dropsBalance.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Drops balance — directly below balance */}
          <LocaleLink
            to="/account/drops"
            role="menuitem"
            className="
              flex items-center gap-3
              px-5 py-2.5
              text-sm font-medium font-sans
              text-ocean-400 dark:text-ocean-300
              hover:bg-sand-50 dark:hover:bg-slate-800
              transition-colors
            "
          >
            <Droplets className="w-4 h-4 flex-shrink-0 text-ocean-400 dark:text-ocean-300" aria-hidden />
            <span>{t('chrome.avatar.dropsWallet')}</span>
          </LocaleLink>

          {/* My Claimed Offers + My Saves */}
          <div className="mx-4 border-t border-slate-100 dark:border-slate-700" />
          <LocaleLink
            to="/account/claimed-offers"
            role="menuitem"
            className="
              flex items-center gap-3
              px-5 py-2.5
              text-sm font-medium font-sans
              text-slate-700 dark:text-white
              hover:bg-sand-50 dark:hover:bg-slate-800
              transition-colors
            "
          >
            <Tag className="w-4 h-4 flex-shrink-0 text-slate-400 dark:text-slate-400" aria-hidden />
            <span>{t('chrome.avatar.claimedOffers')}</span>
          </LocaleLink>
          <LocaleLink
            to="/account/saves"
            role="menuitem"
            className="
              flex items-center gap-3
              px-5 py-2.5
              text-sm font-medium font-sans
              text-slate-700 dark:text-white
              hover:bg-sand-50 dark:hover:bg-slate-800
              transition-colors
            "
          >
            <LifeBuoy className="w-4 h-4 flex-shrink-0 text-slate-400 dark:text-slate-400" aria-hidden />
            <span>{t('chrome.avatar.mySaves')}</span>
          </LocaleLink>

          {/* Thin separator */}
          <div className="mx-4 border-t border-slate-100 dark:border-slate-700 mb-1" />

          {/* Menu items — Profile + Settings only (Drops Wallet moved above) */}
          {([
            { labelKey: 'chrome.avatar.profile',  to: '/profile',  Icon: User,      iconColor: '' },
            { labelKey: 'chrome.avatar.settings', to: '/settings', Icon: Settings2, iconColor: '' },
          ] as const).map(({ labelKey, to, Icon, iconColor }) => (
            <LocaleLink
              key={labelKey}
              to={to}
              role="menuitem"
              className="
                flex items-center gap-3
                px-5 py-2.5
                text-slate-700 dark:text-white text-sm font-medium font-sans
                hover:bg-sand-50 dark:hover:bg-slate-800
                transition-colors
              "
            >
              <Icon
                className={`w-4 h-4 flex-shrink-0 ${iconColor || 'text-slate-400 dark:text-slate-400'}`}
                aria-hidden
              />
              <span>{t(labelKey)}</span>
            </LocaleLink>
          ))}

          {/* Sign out */}
          <button
            role="menuitem"
            onClick={() => void handleSignOut()}
            className="
              flex items-center gap-3
              px-5 py-2.5 w-full text-left
              text-slate-700 dark:text-white text-sm font-medium font-sans
              hover:bg-sand-50 dark:hover:bg-slate-800
              transition-colors
            "
          >
            <LogOut className="w-4 h-4 flex-shrink-0 text-slate-400" aria-hidden />
            <span>{t('chrome.avatar.signOut')}</span>
          </button>

          <div className="flex justify-end px-4 pb-2 pt-1">
            <kbd className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500 leading-none">
              {t('keyboard.esc')}
            </kbd>
          </div>
        </div>
      )}

      {/* Avatar button */}
      <div className="group relative">
        <button
          ref={triggerRef}
          onClick={() => setOpen((prev) => !prev)}
          aria-label={t('chrome.avatar.label')}
          aria-expanded={open}
          className={`
            w-10 h-10 rounded-full
            border-2 transition-all duration-200
            bg-sand-100 dark:bg-slate-700
            overflow-hidden
            flex items-center justify-center
            ${open
              ? 'border-ocean-500 opacity-100 ring-2 ring-ocean-500 ring-offset-2 ring-offset-sand-200 dark:ring-offset-slate-900'
              : 'border-sand-400 opacity-50 group-hover:opacity-100 group-hover:border-slate-400'
            }
          `}
        >
          {user?.image && !imageLoadFailed ? (
            <img
              src={user.image}
              alt={user.name ?? ''}
              className="h-full w-full object-cover"
              onError={() => setImageLoadFailed(true)}
            />
          ) : (
            <span className="text-sm font-medium text-ocean-500 dark:text-ocean-300 font-sans">
              {initial}
            </span>
          )}
        </button>

        {/* Hover tooltip — name + email stacked, M shortcut vertically centered */}
        {!open && (
          <span
            aria-hidden
            className="
              absolute start-full ms-3 top-1/2 -translate-y-1/2
              flex items-center gap-3
              px-3 py-2 rounded-lg
              bg-slate-800 dark:bg-sand-100
              opacity-0 group-hover:opacity-100
              pointer-events-none
              transition-opacity duration-150
              shadow-sm
            "
          >
            {/* Left: name + email stack */}
            <div className="flex flex-col">
              <span className="text-sm font-medium text-white dark:text-slate-800 font-sans leading-tight whitespace-nowrap">
                {user?.name ?? 'Menu'}
              </span>
              {user?.email && (
                <span className="text-[10px] font-mono tracking-wide uppercase text-slate-300 dark:text-slate-500 leading-tight mt-0.5 whitespace-nowrap">
                  {user.email}
                </span>
              )}
            </div>
            {/* Right: M shortcut, vertically centered via items-center on parent */}
            <kbd className="flex-shrink-0 px-1.5 py-0.5 rounded border border-white/30 dark:border-slate-800/30 text-[9px] font-mono leading-none opacity-80 text-white dark:text-slate-800">
              M
            </kbd>
          </span>
        )}
      </div>
      </div>
    </div>
  )
}
